// src/layouts/MainLayout.jsx

import React, { useState, useEffect } from 'react';
import { Box, useMediaQuery, Drawer } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import Sidebar from './Sidebar';
import UserSidebar from '../../components/navigation/UserSidebar';
import NavBreadcrumbs from '../../components/navigation/NavBreadcrumbs';
import QuickActionFab from '../../components/common/QuickActionFab';
import BottomNavigation from '../../components/navigation/BottomNavigation';
import { useUserRoles } from '../../hooks/useUserRoles';

const MainLayout = ({ children, showSidebar, showUserSidebar, showFooter = false }) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const headerHeight = theme.components.MuiLayout.defaultProps.headerHeight;
  const sidebarWidth = theme.components.MuiLayout.defaultProps.sidebarWidth;
  const userRoles = useUserRoles();

  // Mobile drawer state for small screens
  const [mobileOpen, setMobileOpen] = useState(false);

  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;
  // Edge detection zone (in px from left edge)
  const edgeZone = 20;

  // Determine primary user role for bottom navigation
  const getUserRole = () => {
    if (userRoles.includes('Admin')) return 'Admin';
    if (userRoles.includes('PartnerAdmin')) return 'PartnerAdmin';
    return 'User';
  };

  // Toggle mobile drawer state (for the hamburger)
  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  // Close the drawer explicitly
  const handleCloseDrawer = () => {
    setMobileOpen(false);
  };

  // Open the drawer explicitly
  const handleOpenDrawer = () => {
    setMobileOpen(true);
  };

  // Determine which sidebar to show
  const shouldShowSidebar = showSidebar || showUserSidebar;
  const isSidebarShown = shouldShowSidebar && isLargeScreen;

  // Close mobile drawer automatically when route changes
  useEffect(() => {
    handleCloseDrawer();
  }, [location.pathname]);

  // Swipe gesture handlers
  useEffect(() => {
    // Only add swipe handlers on mobile when sidebar is available
    if (!isMobile || !shouldShowSidebar) return;

    const handleTouchStart = (e) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
      setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
      if (!touchStart || !touchEnd) return;
      
      const distance = touchEnd - touchStart;
      const isLeftSwipe = distance < -minSwipeDistance;
      const isRightSwipe = distance > minSwipeDistance;
      const isFromLeftEdge = touchStart < edgeZone;

      // Swipe right from left edge to open drawer
      if (isRightSwipe && isFromLeftEdge && !mobileOpen) {
        handleOpenDrawer();
        // Haptic feedback
        if (navigator.vibrate) {
          navigator.vibrate(10);
        }
      }

      // Swipe left to close drawer
      if (isLeftSwipe && mobileOpen) {
        handleCloseDrawer();
        // Haptic feedback
        if (navigator.vibrate) {
          navigator.vibrate(10);
        }
      }

      // Reset
      setTouchStart(null);
      setTouchEnd(null);
    };

    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    // Cleanup
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile, shouldShowSidebar, touchStart, touchEnd, mobileOpen, minSwipeDistance, edgeZone]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', overflow: 'hidden', width: '100%' }}>
      <Header onToggleSidebar={handleDrawerToggle} />

      {shouldShowSidebar && (
        <>
          {isLargeScreen ? (
            <Box
              component="nav"
              sx={{
                width: sidebarWidth,
                flexShrink: 0,
                position: 'fixed',
                height: '100vh',
                top: 0, // Start from very top
                left: 0,
                bgcolor: '#F4F4F4',
                borderRight: '1px solid',
                borderColor: 'divider',
                zIndex: theme.components.MuiLayout.defaultProps.zIndex.sidebar,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {showSidebar ? <Sidebar /> : <UserSidebar />}
            </Box>
          ) : (
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleCloseDrawer}
              ModalProps={{
                keepMounted: true, // Better mobile performance
              }}
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiDrawer-paper': {
                  width: sidebarWidth,
                  height: '100vh',
                  bgcolor: '#F4F4F4',
                  zIndex: theme.components.MuiLayout.defaultProps.zIndex.sidebar,
                },
              }}
            >
              {showSidebar ? <Sidebar showMobileLogo /> : <UserSidebar showMobileLogo />}
            </Drawer>
          )}
        </>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: `${headerHeight[isLargeScreen ? 'md' : 'xs']}px`,
          pb: isMobile ? '80px' : 0, // Add bottom padding on mobile for bottom nav
          ml: isSidebarShown ? `${sidebarWidth}px` : 0,
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          '& > *': {
            maxWidth: '100%',
          }
        }}
      >
        {/* Show breadcrumbs for user pages */}
        {showUserSidebar && <NavBreadcrumbs />}
        
        {children || <Outlet />}
      </Box>

      {showFooter && <Footer />}
      
      {/* Quick Action FAB for contextual actions */}
      <QuickActionFab />

      {/* Bottom Navigation - Mobile Only */}
      <BottomNavigation userRole={getUserRole()} />
    </Box>
  );
};

export default MainLayout;
